<script>
  import { isBlank, isNotBlank } from 'utils/string';
  import { getSourceSimpleInfoFromEntity } from 'api/source/overall';
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import {
    addAdvInvoiceApp,
    auditAdvInvoiceAppStatus,
    getAdvInvoiceAppPreviewData,
    invoicePreview,
  } from 'api/billing/advertiser/advInvoiceApplicationAPI';
  import { arraysEqual } from 'utils/array';

  const defaultPreviewData = {
    totalAdvRevenue: null,
    totalAdvDeductionAmount: null,
    totalAdvCoverAmount: null,
    totalAdvConfirmedAmount: null,
    checkMessage: null,
  };

  export default {
    name: 'AdvInvoiceApplicationBaseForm',
    props: {
      dialogType: {
        type: String,
        default: 'new',
      },
      dialogVisible: {
        type: Boolean,
        default: false,
      },
      editData: {
        type: Object,
        default: () => {
          return {};
        },
      },
      auditStatus: {
        type: String,
        default: '',
      },
      advEntityList: {
        type: Array,
        default: () => {
          return [];
        },
      },
    },
    mixins: [billingEnumMixins, financeInfoMixins],
    data() {
      return {
        formData: {
          recordDate: null,
          advInvoiceAppNumber: null,
          auditRemark: null,
          remark: null,
          auditStatus: null,
          advIdList: [],
          advEntity: null,
          hyperTapEntityId: null,
          advCompanyPlatform: null,
          advBankId: null,
          hyperTapEntityBankId: null,
          sendEmailList: [''],
        },
        previewData: Object.assign({}, defaultPreviewData),
        advertiserOptions: [],
        bankInfoList: [],
        rules: {
          sendEmailList: [
            {
              validator: (rule, value, callback) => {
                if (value.length === 0 || (value.length === 1 && value[0] === '')) {
                  callback(new Error('At least one email is required.'));
                } else {
                  callback();
                }
              },
            },
          ],
          auditRemark: [],
          remark: [],
          advBankId: [
            { required: true, message: 'Please select adv bank account', trigger: 'change' },
          ],
          hyperTapEntityBankId: [
            { required: true, message: 'Please select hyperTap bank account', trigger: 'change' },
          ],
          hyperTapEntityId: [
            { required: true, message: 'Please select hyperTap entity', trigger: 'change' },
          ],
          advEntity: [{ required: true, message: 'Please select adv entity', trigger: 'change' }],
          advCompanyPlatform: [
            { required: true, message: 'Please select company platform', trigger: 'change' },
          ],
        },
        fileList: {},
        affIds: [],
        formProcessing: false,
        previewWatchField: {
          recordDate: null,
          advCompanyPlatform: null,
          advEntity: null,
          advIdList: [],
        },
      };
    },
    watch: {
      formData: {
        immediate: true,
        handler(val) {
          const { recordDate, advCompanyPlatform, advEntity, advIdList } = val;
          // 值是否发生变化
          if (
            this.previewWatchField.advEntity === advEntity &&
            this.previewWatchField.advCompanyPlatform === advCompanyPlatform &&
            this.previewWatchField.recordDate === recordDate &&
            arraysEqual(advIdList, this.previewWatchField.advIdList)
          ) {
            return;
          }

          if (recordDate && advCompanyPlatform && advEntity) {
            setTimeout(() => {
              if (recordDate && advCompanyPlatform && advEntity) {
                this.handlePreview();
                this.previewWatchField = {
                  recordDate,
                  advCompanyPlatform,
                  advEntity,
                  advIdList,
                };
              }
            }, 500);
          }
        },
        deep: true,
      },
    },
    methods: {
      isNotBlank,
      addEmail() {
        this.formData.sendEmailList.push('');
      },
      removeEmail(index) {
        if (this.formData.sendEmailList && this.formData.sendEmailList.length === 1) {
          if (index === 0) this.$message.error('至少保留一个邮箱地址');
        } else {
          this.formData.sendEmailList.splice(index, 1);
        }
      },
      updateEmail(index, value) {
        this.$set(this.formData.sendEmailList, index, value);
      },
      handleSubmit() {
        this.$refs.baseForm.validate(async (valid) => {
          if (valid) {
            this.formProcessing = true;
            try {
              switch (this.dialogType) {
                case 'new':
                  await addAdvInvoiceApp({ ...this.formData }).then(() => {
                    this.$message({
                      message: 'Success',
                      type: 'success',
                    });
                    this.handleClose();
                    this.$emit('refresh-list');
                  });
                  break;
                case 'status':
                  await auditAdvInvoiceAppStatus({
                    advInvoiceAppNumber: this.formData.advInvoiceAppNumber,
                    auditStatus: this.auditStatus,
                    auditRemark: this.formData.auditRemark,
                  }).then(() => {
                    this.$message({
                      message: 'Success',
                      type: 'success',
                    });
                    this.handleClose();
                    this.$emit('refresh-list');
                  });
                  break;
              }
            } finally {
              setTimeout(() => {
                this.formProcessing = false;
              }, 500);
            }
          } else {
            return false;
          }
        });
      },
      handleClose() {
        this.$emit('update:dialogVisible', false);
        this.$emit('update:changeStatus', '');
        this.$refs.baseForm.resetFields();
        this.advertiserOptions = [];
        this.previewWatchField = {
          recordDate: null,
          advCompanyPlatform: null,
          advEntity: null,
        };
        this.previewData = Object.assign({}, defaultPreviewData);
      },
      handlePreview() {
        const { advEntity, advCompanyPlatform, recordDate, advIdList } = this.formData;
        if (!advEntity || !advCompanyPlatform || !recordDate) {
          this.$message.error('请选择公司平台、公司实体、日期');
          return;
        }
        this.formProcessing = true;
        this.previewData.checkMessage = null;
        this.bankInfoList = [];
        this.formData.advBankId = null;
        this.formData.sendEmailList = [''];
        getAdvInvoiceAppPreviewData({ advEntity, advCompanyPlatform, recordDate, advIdList })
          .then((response) => {
            const result = response.result;
            if (result) {
              this.previewData.totalAdvRevenue = result.totalAdvRevenue;
              this.previewData.totalAdvCoverAmount = result.totalAdvCoverAmount;
              this.previewData.totalAdvConfirmedAmount = result.totalAdvConfirmedAmount;
              this.previewData.totalAdvDeductionAmount = result.totalAdvDeductionAmount;
              this.previewData.checkMessage = result.checkMessage;
              if (result.sendEmailList && result.sendEmailList.length > 0) {
                this.formData.sendEmailList = [
                  ...new Set([...this.formData.sendEmailList, ...result.sendEmailList]),
                ];
              }
              this.bankInfoList = result.bankInfoList;
            }
          })
          .finally(() => {
            setTimeout(() => {
              this.formProcessing = false;
            }, 500);
          });
      },
      handleAdvOptions() {
        this.advertiserOptions = [];
        if (isBlank(this.formData.advEntity) || isBlank(this.formData.advCompanyPlatform)) {
          return;
        }

        this.formData.advIdList = [];
        getSourceSimpleInfoFromEntity({
          companyEntity: this.formData.advEntity,
          companyPlatform: this.formData.advCompanyPlatform,
        }).then((response) => {
          if (response.result) {
            this.advertiserOptions = response.result;
          } else {
            this.advertiserOptions = [];
          }
        });
      },
      handlePreviewInvoice() {
        const {
          recordDate,
          advEntity,
          advCompanyPlatform,
          advIdList,
          hyperTapEntityId,
        } = this.formData;
        if (
          isBlank(recordDate) ||
          isBlank(advEntity) ||
          isBlank(advCompanyPlatform) ||
          hyperTapEntityId === null
        ) {
          this.$message.error('请选择公司平台、公司实体、日期、广告客户、HyperTap实体');
          return;
        }
        this.formProcessing = true;
        invoicePreview({
          recordDate,
          advEntity,
          advCompanyPlatform,
          advIdList,
          hyperTapEntityId,
        }).finally(() => {
          this.formProcessing = false;
        });
      },
    },
  };
</script>

<template>
  <el-dialog
    :title="
      dialogType === 'new' ? 'Adv Invoice Application Create' : 'Adv Invoice Application Update'
    "
    :visible="dialogVisible"
    :close-on-click-modal="false"
    :width="dialogType === 'new' ? '65%' : '35%'"
    @close="handleClose"
  >
    <el-alert
      v-show="isNotBlank(this.previewData.checkMessage)"
      :title="this.previewData.checkMessage"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 20px"
    />
    <el-form
      label-position="left"
      label-width="180px"
      ref="baseForm"
      :model="formData"
      :rules="rules"
      :disabled="formProcessing"
    >
      <!-- 运营审核表单-->
      <section
        v-if="
          dialogType === 'status' && ['APPROVED', 'REJECTED', 'EMAIL_SENT'].includes(auditStatus)
        "
        class="grid-form"
      >
        <el-form-item label="Adv Invoice App Number" prop="advInvoiceAppNumber" required>
          <el-input v-model="formData.advInvoiceAppNumber" placeholder="please input" />
        </el-form-item>
        <el-form-item v-if="auditStatus !== 'EMAIL_SENT'" label="Audit Remark " prop="auditRemark">
          <el-input
            v-model="formData.auditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            label-width="240px"
            placeholder="please input"
          />
        </el-form-item>
      </section>

      <section class="grid-form" v-if="dialogType === 'new'">
        <el-form-item
          label="Application Number"
          prop="advInvoiceAppNumber"
          v-if="dialogType === 'status'"
          required
        >
          <el-input v-model="formData.advInvoiceAppNumber" />
        </el-form-item>
        <el-form-item label="Date" prop="recordDate" required v-if="dialogType === 'new'">
          <el-date-picker v-model="formData.recordDate" value-format="yyyy-MM" type="month" />
        </el-form-item>
        <el-form-item label="Company Platform" prop="advCompanyPlatform">
          <el-select
            v-model="formData.advCompanyPlatform"
            filterable
            clearable
            placeholder="please select"
            @change="handleCompanyPlatformChange(formData.advCompanyPlatform)"
          >
            <el-option
              v-for="item in companyPlatformList"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Adv Entity" prop="advEntity">
          <el-select
            v-model="formData.advEntity"
            ref="companyEntityOptions"
            filterable
            clearable
            placeholder="please select"
            @change="handleAdvOptions"
          >
            <el-option
              v-for="item in specificCompanyEntity"
              :key="item.id"
              :label="item.companyEntity"
              :value="item.companyEntity"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Advertiser" v-if="dialogType === 'new'" prop="advIdList">
          <el-select
            v-model="formData.advIdList"
            filterable
            multiple
            clearable
            :loading="formProcessing"
          >
            <el-option
              v-for="item in advertiserOptions"
              :key="item.id"
              :label="item.id + ' - ' + item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Adv Revenue" prop="advRevenue">
          <el-input v-model="previewData.totalAdvRevenue" disabled />
        </el-form-item>
        <el-form-item label="Adv Deduction Amount" prop="advDeductionAmount">
          <el-input v-model="previewData.totalAdvDeductionAmount" disabled />
        </el-form-item>
        <el-form-item label="Adv Cover Amount" prop="advCoverAmount">
          <el-input v-model="previewData.totalAdvCoverAmount" disabled />
        </el-form-item>
        <el-form-item label="Adv Confirmed Amount" prop="advConfirmedAmount">
          <el-input v-model="previewData.totalAdvConfirmedAmount" disabled />
        </el-form-item>
        <el-form-item label="Adv Bank Account" prop="advBankId">
          <el-select v-model="formData.advBankId" filterable clearable :loading="formProcessing">
            <el-option
              v-for="item in bankInfoList"
              :key="item.bankId"
              :label="item.bankAccountName"
              :value="item.bankId"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="HyperTap Entity" prop="hyperTapEntityId" required>
          <el-select
            v-model="formData.hyperTapEntityId"
            ref="hyperTapEntityOptions"
            filterable
            placeholder="please select"
            @change="handleHyperTapEntityChange(formData.hyperTapEntityId)"
          >
            <el-option
              v-for="item in hyperTapCompanyEntities"
              :key="item.bfiId"
              :label="
                isNotBlank(item.companyEntityAlias) ? item.companyEntityAlias : item.companyEntity
              "
              :value="item.bfiId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="HyperTap Bank Account" prop="hyperTapEntityBankId">
          <el-select
            v-model="formData.hyperTapEntityBankId"
            ref="hyperTapEntityBankOptions"
            filterable
            placeholder="please select"
          >
            <el-option
              v-for="item in specificHyperTapBankInfoList"
              :key="item.bankId"
              :label="item.bankAccountName"
              :value="item.bankId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Send Emails" v-if="dialogType === 'new'" prop="sendEmailList" required>
          <div class="multi-item-column">
            <div
              v-for="(input, index) in formData.sendEmailList"
              :key="index"
              class="multi-item-row"
            >
              <el-input
                :value="input"
                @input="updateEmail(index, $event)"
                placeholder="please input"
              ></el-input>
              <el-button
                type="danger"
                icon="el-icon-delete"
                @click="removeEmail(index)"
                circle
              ></el-button>
              <el-button
                @click="addEmail"
                icon="el-icon-plus"
                type="primary"
                style="margin-left: 0"
                v-if="index === 0"
                circle
              />
            </div>
          </div>
        </el-form-item>
        <el-form-item label="Invoice Preview">
          <el-button
            type="primary"
            icon="el-icon-search"
            @click="handlePreviewInvoice"
            :loading="formProcessing"
            >Preview</el-button
          >
        </el-form-item>
        <el-form-item label="Remark" prop="remark" v-if="dialogType !== 'status'">
          <el-input type="textarea" maxlength="255" show-word-limit v-model="formData.remark" />
        </el-form-item>
        <el-form-item
          label="Audit Remark"
          prop="auditRemark"
          v-if="dialogType === 'status' && changeStatus !== 'EMAIL_SENT'"
        >
          <el-input
            v-model="formData.auditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
          ></el-input>
        </el-form-item>
      </section>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" :loading="formProcessing" @click="handleSubmit">确 定</el-button>
    </span>
  </el-dialog>
</template>

<style scoped lang="scss"></style>
